// src/components/BuscadorEquipo.js
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Input, Label, Icon } from "semantic-ui-react";

const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // quita tildes
    .trim();

const buildSearchText = (e) =>
  norm(
    [
      e.id,
      e.tipo,
      e.marca,
      e.modelo,
      e.serie,
      e.familia,
    ].filter(Boolean).join(" ")
  );

const buildLabel = (e) =>
  `${e.tipo || ""} ${e.marca || ""} ${e.modelo || ""}`.trim();

const BuscadorEquipo = ({
  equipos = [],
  onSelectEquipo,
  disabled = false,
  value = "",
  onChangeValue,
  placeholder = "Buscar equipo por tipo/marca/modelo/serie/familia o ID…",
  autoSelectUnico = true,
  maxResults = 25,
}) => {
  const [queryText, setQueryText] = useState(value);
  const [open, setOpen] = useState(false);

  useEffect(() => setQueryText(value), [value]);

  const results = useMemo(() => {
    const q = norm(queryText);
    if (!q) return [];
    const filtered = equipos
      .map((e) => ({ ...e, _search: buildSearchText(e) }))
      .filter((e) => e._search.includes(q))
      .slice(0, maxResults);

    return filtered;
  }, [equipos, queryText, maxResults]);

  const handleChange = (e) => {
    const v = e.target.value;
    setQueryText(v);
    onChangeValue?.(v);
    setOpen(!!v);
  };

  const select = (equipo) => {
    setQueryText(equipo.serie || equipo.id);
    onChangeValue?.(equipo.serie || equipo.id);
    setOpen(false);
    onSelectEquipo?.(equipo);
  };

  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (!autoSelectUnico) return;
    if (results.length === 1) select(results[0]);
  };

  return (
    <Wrap>
      <Input
        fluid
        disabled={disabled}
        icon="search"
        placeholder={placeholder}
        value={queryText}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={() => {
          if (queryText?.trim()) setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
      />

      {open && results.length > 0 && (
        <Lista>
          {results.map((e) => (
            <Item
              key={e.id}
              onMouseDown={(ev) => ev.preventDefault()}
              onClick={() => select(e)}
              title={`${buildLabel(e)} | Serie: ${e.serie || "-"} | Fam: ${e.familia || "-"} | ID: ${e.id}`}
            >
              <TopRow>
                <strong>{buildLabel(e) || "Equipo"}</strong>
                <Label size="tiny" basic>
                  <Icon name="tag" />
                  {e.familia || "Sin familia"}
                </Label>
              </TopRow>

              <BottomRow>
                <span><b>Serie:</b> {e.serie || "-"}</span>
                <span><b>ID:</b> {e.id}</span>
              </BottomRow>
            </Item>
          ))}
        </Lista>
      )}
    </Wrap>
  );
};

export default BuscadorEquipo;

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;

const Lista = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 9999;

  background: #fff;
  border: 1px solid #ddd;
  border-radius: 0.75rem;
  margin: 0;
  padding: 0;
  list-style: none;

  max-height: 260px;
  overflow: auto;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.12);
`;

const Item = styled.li`
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f1f1f1;

  &:hover {
    background: #eaffff;
  }
  &:last-child {
    border-bottom: none;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const BottomRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
  font-size: 12px;
  opacity: 0.85;
`;
